import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, Mail, Copy, Check, Briefcase, MapPin,
  Heart, Globe2, Clock,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const CAREERS_EMAIL = 'careers@metaxuone.com';

const IMG_CULTURE  = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80';
const IMG_SOCIETY  = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&q=80';
const IMG_FLEX     = 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=1600&q=80';

const SECTIONS = [
  {
    id: 'culture',
    eyebrow: 'Company culture',
    icon: Heart,
    title: ['Curiosity, calm,', 'and craft.'],
    body:  'We hire senior people and trust them. Small teams own outcomes end-to-end. We default to writing — short docs over long meetings — so the work, not the room, decides what ships.',
    image: IMG_CULTURE,
    reverse: false,
    points: [
      { t: 'Weekly teach & demo',     d: 'Everyone presents — engineers, designers, ops. Show your work, ask hard questions.' },
      { t: 'Library + learning fund', d: 'Books, courses, conferences. If it sharpens you, it sharpens us.' },
      { t: 'Quarterly offsites',      d: 'Two days, one place. Sketch the next quarter, then go eat together.' },
      { t: 'Kindness as default',     d: 'Direct on the work, gentle on the human. No bro culture, no theatre.' },
    ],
  },
  {
    id: 'society',
    eyebrow: 'Society contribution',
    icon: Globe2,
    title: ['Building beyond', 'the balance sheet.'],
    body:  'Technology that runs critical operations should give back to the communities it depends on. We commit time, money, and our platform to programs that compound — open source, education, and non-profits doing real work.',
    image: IMG_SOCIETY,
    reverse: true,
    points: [
      { t: 'Paid volunteer days',     d: 'Five days a year, on the company. Pick a cause that matters to you.' },
      { t: 'Open-source quarterly',   d: 'A grant pool for the OSS tools we (and our customers) depend on.' },
      { t: 'Mentorship',              d: 'Engineering mentorship for students at local universities and bootcamps.' },
      { t: 'Free licenses · non-profits', d: 'MetaxuOne — operational intelligence — at no cost for qualifying non-profits.' },
    ],
  },
  {
    id: 'flexible-working',
    eyebrow: 'Flexible working',
    icon: Clock,
    title: ['Work that works', 'around your life.'],
    body:  'In-person where collaboration compounds, remote where focus does. We trust outcomes, not seat-time — and we pay you well to make great choices about both.',
    image: IMG_FLEX,
    reverse: false,
    points: [
      { t: 'Hybrid · Office + remote', d: 'In the office for kickoffs, design crit, demos. Remote for deep work.' },
      { t: 'Flexible hours',              d: 'Core overlap 11:00–15:00 IST. The rest of the day is yours.' },
      { t: 'Generous PTO + holidays',     d: 'Take it. Truly. Leadership models it first.' },
      { t: 'Home-office stipend',         d: 'A one-time setup budget when you join — chair, desk, monitor, whatever you need.' },
    ],
  },
];

const CareersContactCard = () => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(CAREERS_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {/* ignore */}
  };
  return (
    <div className="relative rounded-3xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-7 lg:p-9 overflow-hidden" data-testid="careers-contact-card">
      <div aria-hidden className="absolute -top-20 -right-16 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.20), transparent 70%)' }} />
      <div className="relative">
        <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Apply now</p>
        <h3 className="mt-3 text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
          Send your CV to <span className="brand-gradient-text italic font-light">careers@metaxuone.com</span>
        </h3>
        <p className="mt-4 text-[15px] text-neutral-300 max-w-2xl leading-relaxed">
          Include the area you&rsquo;re interested in, a short note on what excites you about MetaxuOne, and a way to see your work — GitHub, portfolio, paper, anything that&rsquo;s yours.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Button asChild className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2" data-testid="careers-email-btn">
            <a href={`mailto:${CAREERS_EMAIL}?subject=Application%20—%20MetaxuOne`}>
              <Mail className="w-4 h-4" /> Email us
            </a>
          </Button>
          <button
            type="button"
            onClick={onCopy}
            className="h-12 px-5 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center gap-2"
            data-testid="careers-copy-email-btn"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied' : 'Copy email'}
          </button>
          <span className="text-[12px] text-neutral-500 ml-1 select-all tabular-nums">{CAREERS_EMAIL}</span>
        </div>
      </div>
    </div>
  );
};

const SectionRow = ({ s }) => {
  const Icon = s.icon;
  return (
    <section id={s.id} className="relative bg-black border-b border-neutral-900 scroll-mt-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className={`grid lg:grid-cols-12 gap-10 lg:gap-14 items-center ${s.reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
              <Icon className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">{s.eyebrow}</span>
            </div>
            <h2 className="mt-4 text-[36px] sm:text-[44px] lg:text-[54px] leading-[1.04] tracking-[-0.025em] font-semibold text-white">
              {s.title[0]} <br />
              <span className="italic font-light brand-gradient-text">{s.title[1]}</span>
            </h2>
            <p className="mt-5 text-[16px] lg:text-[17px] text-neutral-300 leading-relaxed max-w-xl">{s.body}</p>
            <ul className="mt-7 space-y-4 max-w-xl">
              {s.points.map((p) => (
                <li key={p.t} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                  <div>
                    <div className="text-[14.5px] font-semibold text-white">{p.t}</div>
                    <div className="text-[13px] text-neutral-400 leading-relaxed">{p.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden border border-neutral-800 aspect-[4/3] bg-neutral-950 group">
              <img src={s.image} alt={s.eyebrow} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/30 to-transparent" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-2xl pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.18em] text-neutral-300/90">{s.eyebrow}</span>
                <ArrowRight className="w-4 h-4 text-neutral-300/90" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Careers = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-black min-h-screen" data-testid="careers-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 20%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-20">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Company</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">Careers</span>
            </nav>
            <div className="mt-7 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                  <Briefcase className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">Careers at MetaxuOne</span>
                </div>
                <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[76px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                  Build the future of <br />
                  <span className="italic font-light brand-gradient-text">autonomous operations.</span>
                </h1>
                <p className="mt-6 text-[17px] lg:text-[18px] text-neutral-300 leading-relaxed max-w-2xl">
                  We&rsquo;re a small, senior team in Mumbai building the platform that turns enterprise operations from reactive to autonomous. If that sounds like the next thing you want to ship — we want to hear from you.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2" data-testid="careers-hero-email-btn">
                    <a href={`mailto:${CAREERS_EMAIL}?subject=Application%20—%20MetaxuOne`}>
                      <Mail className="w-4 h-4" /> Email careers@metaxuone.com
                    </a>
                  </Button>
                  <a href="#culture" className="h-12 px-6 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center" data-testid="careers-explore-link">
                    Explore life here
                  </a>
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-6">
                  <div className="flex items-center gap-2 text-blue-300">
                    <MapPin className="w-4 h-4" />
                    <span className="text-[12.5px] uppercase tracking-[0.16em]">Based in</span>
                  </div>
                  <p className="mt-3 text-[20px] font-semibold text-white">Mumbai, Maharashtra · India</p>
                  <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">In-person where it matters, remote-friendly for focused work. Open to candidates across India and select global hires.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY METAXUONE — hidden per request */}
        {/* OPEN AREAS — hidden per request */}

        {/* COMPANY CULTURE · SOCIETY · FLEXIBLE WORKING */}
        {SECTIONS.map((s) => <SectionRow key={s.id} s={s} />)}

        {/* CONTACT CARD */}
        <section className="relative bg-neutral-950">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <CareersContactCard />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;
